<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb
        :items="[{ text: 'Reports', path: { name: 'admin-reports' } }]"
        name="Inventory Coverage"
        :title="pageTitle"
    />

    <admin-panel with-header>
      <b-table
          :fields="reportColumns"
          :items="reportRows"
          table-class="text-nowrap"
          responsive
          hover
          show-empty
          empty-text="No data found"
      >
        <template #cell(coverage_percent)="data">
          <span
              class="coverage-badge"
              :class="getCoverageClass(data.item.coverage_percent)"
          >
            {{ formatPercent(data.item.coverage_percent) }}
          </span>
        </template>
      </b-table>

      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="text-muted">
          </div>

          <div class="d-flex">
            <b-btn
                variant="primary"
                class="mr-2"
                v-b-tooltip.hover
                title="Download CSV"
                @click.prevent="downloadCsv"
            >
              <b-icon-download/>
            </b-btn>

            <b-btn
                variant="primary"
                v-b-tooltip.hover
                title="Reload Report"
                @click.prevent="loadReportData"
            >
              <b-icon-arrow-clockwise/>
            </b-btn>
          </div>
        </div>
      </template>
    </admin-panel>
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";

export default {
  name: "AdminReportInventoryCoverage",
  components: {AdminPanel, AdminBreadcrumb, AdminPreloader},
  data() {
    return {
      loading: true,
      report_data: [],
    };
  },
  methods: {
    loadReportData() {
      const reportUrl = API_ROUTES.ADMIN_REPORT_SELLERS_INVENTORY_COVERAGE;

      this.loading = true;

      this.$api.get(reportUrl)
          .then(({data}) => {
            this.report_data = Array.isArray(data.report_data) ? data.report_data : [];
          })
          .catch(() => {
            this.$bvToast.toast('Failed to load report', {
              title: 'Error!',
              variant: 'danger'
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    formatPercent(value) {
      if (value === null || typeof value === 'undefined' || value === '') {
        return '-';
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return '-';
      }

      return `${numericValue.toFixed(2)}%`;
    },
    getCoverageClass(value) {
      const numericValue = Number(value);

      if (value === null || typeof value === 'undefined' || Number.isNaN(numericValue)) {
        return 'coverage-badge--empty';
      }

      if (numericValue < 40) {
        return 'coverage-badge--danger';
      }

      if (numericValue < 70) {
        return 'coverage-badge--warning';
      }

      return 'coverage-badge--success';
    },
    downloadCsv() {
      if (!this.reportRows.length) {
        return;
      }

      const formatCsvPercent = (value) => {
        if (value === null || typeof value === 'undefined' || value === '') {
          return '';
        }

        const numericValue = Number(value);

        if (Number.isNaN(numericValue)) {
          return '';
        }

        return `${numericValue.toFixed(2)}%`;
      };

      let csvContent = "data:text/csv;charset=utf-8,";
      const headers = [
        'Seller',
        'Active Listings',
        'Listed Products',
        'Total Inventory',
        'Coverage %'
      ];

      csvContent += headers.join(",") + "\r\n";

      this.reportRows.forEach((row) => {
        const rowData = [
          `"${row.seller_name ?? ''}"`,
          row.active_listings ?? 0,
          row.total_inventory ?? 0,
          `"${formatCsvPercent(row.coverage_percent)}"`
        ];

        csvContent += rowData.join(",") + "\r\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");

      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "gl_inventory_coverage.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  computed: {
    pageTitle() {
      return 'Inventory Coverage';
    },

    reportColumns() {
      return [
        {
          key: "seller_name",
          label: "Seller",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "active_listings",
          label: "Active Listings",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "total_inventory",
          label: "Total Inventory",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "coverage_percent",
          label: "Coverage %",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ];
    },
    reportRows() {
      if (!Array.isArray(this.report_data)) {
        return [];
      }

      return this.report_data.map((item) => ({
        seller_id: item.seller_id ?? null,
        seller_name: item.seller_name ?? 'Unknown',
        active_listings: Number(item.active_listings ?? 0),
        total_inventory: Number(item.total_inventory ?? 0),
        coverage_percent: item.coverage_percent,
      }));
    }
  },
  created() {
    this.loadReportData();
  }
}
</script>

<style scoped>
.coverage-badge {
  display: inline-block;
  min-width: 82px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  text-align: center;
}

.coverage-badge--danger {
  color: #842029;
  background-color: #f8d7da;
}

.coverage-badge--warning {
  color: #664d03;
  background-color: #fff3cd;
}

.coverage-badge--success {
  color: #0f5132;
  background-color: #d1e7dd;
}

.coverage-badge--empty {
  color: #6c757d;
  background-color: #e9ecef;
}
</style>